import { ReactNode, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import Button from '~/shared/components/Buttons';
import ClipboardIcon from '~/shared/assets/icons/clipboard.svg';
import InfoIcon from '~/shared/assets/icons/info.svg';

import { CopiedTooltip } from './CopiedTooltip';

export function SectionFooter({
  infoProps: { variant = 'default', text },
  copyText,
  buttonEl,
}: {
  infoProps: { variant?: 'default' | 'success' | 'error' | 'none'; text?: string | ReactNode };
  copyText?: string;
  buttonEl?: ReactNode;
}) {
  const [copied, setCopied] = useState('');
  const timeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!copied) {
      return;
    }
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      setCopied('');
      timeout.current = null;
    }, 750);
  }, [copied]);

  const variantsMap: Record<typeof variant, { fill: string; text: string; bg: string }> = {
    default: { fill: 'fill-primary-400', text: 'text-primary-400', bg: '' },
    success: { fill: 'fill-[#06D727]', text: 'text-[#06D727]', bg: 'bg-[#06D727]/[0.1]' },
    error: { fill: 'fill-[#FA1649]', text: 'text-[#FA1649]', bg: 'bg-[#FA1649]/[0.1]' },
    none: { fill: 'fill-primary-400', text: 'text-primary-400', bg: '' },
  };

  return (
    <div className="w-full flex items-center justify-between mt-[8px]">
      {variant === 'none' ? (
        text
      ) : (
        <div
          className={clsx('flex items-center gap-[12px]', variantsMap[variant]?.bg, {
            'px-[15px] py-[10px] rounded-[4px]': variant && variant !== 'default',
          })}
        >
          <InfoIcon className={clsx('w-[24px] h-[24px]', variantsMap[variant]?.fill)} />
          <div className={clsx('text-[14px] leading-[20px]', variantsMap[variant]?.text)}>
            {text}
          </div>
        </div>
      )}
      {buttonEl ? (
        buttonEl
      ) : copyText ? (
        <Button
          type="button"
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigator.clipboard.writeText(copyText).then(() => setCopied(copyText));
          }}
          className="w-[121px] gap-[10px] relative"
        >
          <div className="flex items-center gap-[10px]">
            <ClipboardIcon className="w-[24px] h-[24px] stroke-primary-900" />
            Copy
          </div>
          {copied === copyText && <CopiedTooltip />}
        </Button>
      ) : undefined}
    </div>
  );
}
